import { useContext } from 'react';
import { Typography, Grid } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useQuery } from '@apollo/client';

import { AppContext } from '../context/app-context';
import { ServiceMethod } from '../types/order';
import { CollectionPoint, DeliveryArea, Venue } from '../types';
import { GET_VENUE } from '../graphql/query';
import { customTheme } from '../theme';
import { toTitleCase } from '../utils/string';

export const LocationText = () => {
  const { t } = useTranslation(['patron_portal']);

  const {
    serviceMethod,
    sections,
    section,
    row,
    seat,
    stadium,
    collectionPoint,
    deliveryArea,
  } = useContext(AppContext);

  const { data: venueData } = useQuery(GET_VENUE, {
    variables: { id: stadium?.value },
    skip: !stadium?.value,
  });

  const venue: Venue | undefined = venueData?.getVenue;

  const selectedSectionLabel = sections?.value?.find(
    (sec) => sec.id === section?.value,
  )?.label;

  const selectedCollectionPoint = venue?.collectionPoints?.find(
    (cp: CollectionPoint) => cp.id === collectionPoint?.value,
  )?.label;

  const selectedDeliveryArea = venue?.deliveryAreas?.find(
    (da: DeliveryArea) => da.id === deliveryArea?.value,
  )?.label;

  let locationText: { key: string; params: Record<string, any> }[] = [];

  switch (serviceMethod?.value) {
    case ServiceMethod.VENDOR_DELIVERY:
      locationText = [
        {
          key: 'patron_portal.order.msgLocationVendorDelivery_location',
          params: { sectionName: toTitleCase(selectedSectionLabel) },
        },
        {
          key: 'patron_portal.order.msgLocationVD_daName',
          params: {
            deliveryArea: toTitleCase(selectedDeliveryArea),
          },
        },
      ];
      break;

    case ServiceMethod.ROOM_SERVICE:
      locationText = [
        {
          key: 'patron_portal.order.msgLocationRoomService_room',
          params: { roomNumber: toTitleCase(seat?.value) },
        },
      ];
      break;

    case ServiceMethod.TABLE_SERVICE:
      locationText = [
        {
          key: 'patron_portal.order.msgLocationDineIn_section',
          params: { tableNumber: toTitleCase(seat?.value) },
        },
      ];
      break;

    case ServiceMethod.COLLECT_FROM_VENDOR:
      locationText = [
        {
          key: 'patron_portal.order.locationInfoCollectFromVendor_section',
          params: { sectionName: toTitleCase(selectedSectionLabel) },
        },
      ];
      break;

    case ServiceMethod.COLLECTION_POINT:
      locationText = [
        {
          key: 'patron_portal.order.msgLocationCP_section',
          params: {
            sectionName: toTitleCase(selectedSectionLabel),
          },
        },
        {
          key: 'patron_portal.order.msgLocationCP_cpName',
          params: {
            collectionPointName: toTitleCase(selectedCollectionPoint),
          },
        },
      ];
      break;

    default:
      locationText = [
        {
          key: 'patron_portal.order.msgLocationTTS_section',
          params: {
            sectionName: toTitleCase(selectedSectionLabel),
          },
        },
        {
          key: 'patron_portal.order.msgLocationTTS_row',
          params: {
            row: toTitleCase(row?.value),
          },
        },
        {
          key: 'patron_portal.order.msgLocationTTS_seat',
          params: {
            seat: toTitleCase(seat?.value),
          },
        },
      ];
  }

  return (
    <Grid
      item
      xs={12}
      display="flex"
      width="fit-content"
      margin="auto"
      flexDirection="column"
    >
      {locationText.map(({ key, params }) => (
        <Typography
          key={key}
          fontFamily={customTheme.fontFamily}
          fontSize={14}
          fontWeight={500}
        >
          {t(key, { ns: 'patron_portal', ...params })}
        </Typography>
      ))}
    </Grid>
  );
};
